import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { layoutService, imageService } from '@bedrock/fido-sdk';
import {
  DEFAULT_IMAGE_FORMAT,
  IMAGE_FIT_SCALE_CROP,
  IMAGE_QUALITY,
  IMAGE_FIT_MAX,
  TRANSPARENT_IMAGE_FORMAT,
} from '@bedrock-rdk/constants';
import { getCSAPicto, getServicePicto } from '@bedrock-rdk/utils';
import Icon from '../../../../icon/icon.component';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL } from '../../../../signals';
import { Button } from '../../../../button/button.component';
import { CallToAction as CallToActionPrimary } from '../../../../callToAction/primary/callToAction.component';
import { CallToAction as CallToActionSecondary } from '../../../../callToAction/secondary/callToAction.component';
import Progress from '../../../../progress/progress.component';
import { makeProgressBarProperties } from '../../../../progress/progress.utils';
import { ProgramCardError } from './programCardError/programCardError.component';
import { handleInteraction } from '../../../../../app/utils/app.utils';
import { HIDE_POPUP_SIGNAL } from '../../../../../popupContainer/popupContainer.constants';
import { makeBookmarkAction } from '../../../../myListCallToAction/myListCallToAction.utils';
import CTA from '../../../../../cta/cta.constants';
import { sendEvent } from '../../../../../../logic/analytics/analytics.utils';
import { contain } from '../../../../utils/textures';
import { isActionAllowed } from '../epg.utils';
import {
  defaultContext,
  getInitialStyle,
  getBasicIconViewBox,
  LOGO_DIMENSIONS,
  IMAGE_DIMENSIONS,
} from './programCard.style';
import { SECTION_CODE, MY_LIST_ICONS } from './programCard.constants';

export class ProgramCard extends Lightning.Component {
  static _template() {
    return _merge(
      {
        passSignals: {
          [HIDE_POPUP_SIGNAL]: true,
        },
        Background: {},
        Image: {},
        Gradient: {},
        Container: {
          LogoWrapper: {
            Logo: {},
          },
          Title: {},
          ProgramCardError: {
            visible: false,
            type: ProgramCardError,
          },
          CTAContainer: {
            visible: false,
            PlayButton: {
              type: CallToActionPrimary,
            },
            MyListButton: {
              visible: false,
              type: CallToActionSecondary,
            },
          },
          DetailsContainer: {
            Details: {},
            Time: {},
            ProgressBar: {
              visible: false,
              type: Progress,
            },
            Description: {},
            ExtraDetails: {},
            IconRow: {
              ServiceIcon: {
                type: Icon,
              },
              Picto1: {
                type: Icon,
              },
            },
            Button: {
              visible: false,
              type: Button,
            },
          },
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
  }

  _init() {
    this._index = 0;
    this._focused = this.tag('CTAContainer').children[this._index];
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  _detach() {
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
    this._focused = undefined;
  }

  get blockId() {
    return this._blockId;
  }

  set blockId(value) {
    this._blockId = value;
    this._getDataForCard(value);
  }

  _showEpgModal(content) {
    this.content = content;
    const { screenSize } = this._style;
    const {
      title,
      details,
      extraDetails,
      action,
      pictos,
      progressBar,
      logo,
      image,
      contentAdvisory,
      description,
      secondaryActions,
      bookmark,
    } = this.content;

    // Icons
    const csaPicto = getCSAPicto(contentAdvisory.pictos);
    const servicePicto = getServicePicto(pictos);

    const isBasicIconVisible = Boolean(csaPicto);
    const serviceIconVisible = Boolean(servicePicto);
    const logoSrc = imageService.getUrl(
      logo.id,
      LOGO_DIMENSIONS[screenSize].width,
      LOGO_DIMENSIONS[screenSize].height,
      {
        fit: IMAGE_FIT_MAX,
        quality: IMAGE_QUALITY,
        format: TRANSPARENT_IMAGE_FORMAT,
      },
    );
    const myListItem = secondaryActions[0];
    const isPrimaryActionSupported = isActionAllowed(action);

    const patch = {
      Image: {
        src: imageService.getUrl(image.id, IMAGE_DIMENSIONS[screenSize].width, IMAGE_DIMENSIONS[screenSize].height, {
          fit: IMAGE_FIT_SCALE_CROP,
          quality: IMAGE_QUALITY,
          format: DEFAULT_IMAGE_FORMAT,
        }),
      },
      Gradient: {},
      Container: {
        LogoWrapper: {
          Logo: {
            texture: {
              ...contain(logoSrc, LOGO_DIMENSIONS[screenSize].width, LOGO_DIMENSIONS[screenSize].height),
              hasAlpha: true,
            },
          },
        },
        Title: { visible: Boolean(title), text: { text: title } },
        ProgramCardError: {
          visible: false,
          type: ProgramCardError,
        },
        CTAContainer: {
          visible: Boolean(action && isPrimaryActionSupported),
          PlayButton: {
            visible: isPrimaryActionSupported,
            label: action ? action.label : '',
            picto: action ? action.picto : '',
          },
          MyListButton: {
            visible: Boolean(myListItem),
            picto: myListItem ? myListItem.picto : '',
          },
        },
        DetailsContainer: {
          Details: {
            visible: Boolean(details),
            text: { text: details },
          },
          Time: {},
          ProgressBar: {
            ...makeProgressBarProperties(progressBar),
          },
          Description: {
            visible: Boolean(description),
            text: { text: description },
          },
          ExtraDetails: {
            visible: Boolean(extraDetails),
            text: { text: extraDetails },
          },
          IconRow: {
            ServiceIcon: {
              visible: serviceIconVisible,
              iconData: {
                picto: servicePicto,
                viewBox: getBasicIconViewBox(screenSize),
              },
            },
            Picto1: {
              visible: isBasicIconVisible,
              iconData: {
                picto: csaPicto,
                viewBox: getBasicIconViewBox(screenSize),
              },
            },
          },
          Button: {
            visible: true,
            label: secondaryActions[1].label,
          },
        },
      },
    };

    this.patch(patch);

    if (!action) {
      this._handleNoContentState(secondaryActions);
    }

    if (!this.tag('CTAContainer').visible) {
      this._index = undefined;
      this._focused = this.tag('Button');
    }
    this._refocus();

    if (bookmark) {
      bookmark.action = makeBookmarkAction(bookmark.id, bookmark.state ? CTA.UNBOOKMARK : CTA.BOOKMARK);
    }

    sendEvent({ key: 'epgModalOpen' });
  }

  async _getDataForCard(id) {
    await layoutService
      .fetchEpgModal({
        SECTION_CODE,
        id,
      })
      .then(response => this._showEpgModal(response.blocks[0].content.items[0].itemContent))
      .catch(error => this._handleErrorState(error));
  }

  _handleErrorState(error) {
    // eslint-disable-next-line no-console
    console.log('Handle error', error);
  }

  _handleNoContentState(context) {
    this.patch({
      Container: {
        CTAContainer: { visible: false },
        ProgramCardError: { visible: true },
        DetailsContainer: {
          Button: {
            visible: true,
            label: context[1].label,
          },
        },
      },
    });
  }

  _getFocused() {
    return this._focused;
  }

  _handleRight() {
    if (this._index < this.tag('CTAContainer').children.filter(child => child.visible).length - 1) {
      this._index += 1;
      this._setIndex(this._index);

      return true;
    }

    return false;
  }

  _handleLeft() {
    if (this._index > 0) {
      this._index -= 1;
      this._setIndex(this._index);

      return true;
    }

    return false;
  }

  _setIndex(index) {
    this._focused = this.tag('CTAContainer').children[index];
  }

  _handleDown() {
    if (this.tag('Button').visible) {
      this._focused = this.tag('Button');
    }

    return true;
  }

  _handleUp() {
    if (this.tag('CTAContainer').visible) {
      this._focused = this.tag('CTAContainer').children[this._index];
    }

    return true;
  }

  _handleEnter() {
    if (this._focused === this.tag('PlayButton')) {
      handleInteraction(this.content, this);
      this.signal(HIDE_POPUP_SIGNAL);
    } else if (this._focused === this.tag('MyListButton')) {
      handleInteraction(this.content.bookmark, this);
      // eslint-disable-next-line
      this.picto = this.content.bookmark.state
        ? MY_LIST_ICONS.MY_LIST_ICON_UNBOOKED
        : MY_LIST_ICONS.MY_LIST_ICON_BOOKED;
      this.tag('Container.CTAContainer.MyListButton').patch({ picto: this.picto });
      this._modifyBookmark();
    } else if (this._focused === this.tag('Container.DetailsContainer.Button')) {
      this.content.action = this.content.secondaryActions[1];
      handleInteraction(this.content, this);
      this.signal(HIDE_POPUP_SIGNAL);
    }

    sendEvent({ key: 'epgModalClose' });

    return true;
  }

  _modifyBookmark() {
    if (this.content.bookmark) {
      this.content.bookmark.state = !this.content.bookmark.state;
      // eslint-disable-next-line
      this.content.bookmark.action = makeBookmarkAction(this.content.bookmark.id, this.content.bookmark.state ? CTA.UNBOOKMARK : CTA.BOOKMARK);
    }
  }
}
